'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import type * as React from 'react';
import { useEffect } from 'react';

import { Icon, type Icons } from '@/components/icon';
import { Button, Kbd } from '@/components/ui/design-system';
import { detectOS } from '@/lib/platform';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogClose,
  DialogPortal,
  DialogScrollArea,
  DialogTitle,
  DialogTrigger,
} from './dialog';
import { H6 } from './text';

// Modal Root - wraps Dialog with modal={false} to allow dialer interaction
function Modal({ ...props }: React.ComponentProps<typeof Dialog>) {
  return <Dialog data-slot="modal" modal={false} {...props} />;
}

// Modal Trigger - wraps DialogTrigger
function ModalTrigger({ ...props }: React.ComponentProps<typeof DialogTrigger>) {
  return <DialogTrigger data-slot="modal-trigger" {...props} />;
}

// Modal Container (the actual modal box)
interface ModalContainerProps {
  children: React.ReactNode;
  className?: string;
}

function ModalContainer({ children, className }: ModalContainerProps) {
  return (
    <DialogPortal>
      {/* Custom overlay - DialogClose makes it close on click, but dialer has higher z-index so clicks there won't reach this */}
      <DialogClose asChild>
        <div
          data-slot="modal-overlay"
          className="fixed inset-0 z-overlay bg-primary-100/20 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 cursor-default"
        />
      </DialogClose>
      {/* Using DialogPrimitive.Content directly because DialogContent includes Portal+Overlay which we handle separately */}
      <DialogPrimitive.Content
        data-slot="modal-container"
        aria-describedby={undefined}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
        className={cn(
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'fixed top-1/2 left-1/2 z-modal -translate-x-1/2 -translate-y-1/2',
          'flex w-[480px] flex-col overflow-hidden rounded-2xl border border-primary-10 bg-white shadow-lg',
          'focus-visible:outline-none',
          className,
        )}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

// Modal Header - contains DialogTitle for accessibility
interface ModalHeaderProps {
  icon: Icons;
  title: string;
  onClose: () => void;
  className?: string;
}

function ModalHeader({ icon, title, onClose, className }: ModalHeaderProps) {
  return (
    <div
      data-slot="modal-header"
      className={cn(
        'flex shrink-0 items-center justify-between overflow-hidden border-b border-primary-5 bg-white p-3',
        className,
      )}
    >
      <div className="flex items-center gap-1">
        <Icon name={icon} className="text-m text-primary-100" />
        <DialogTitle asChild>
          <H6 className="text-primary-100">{title}</H6>
        </DialogTitle>
      </div>
      <DialogClose asChild>
        <button
          type="button"
          onClick={onClose}
          className="flex size-5 cursor-pointer items-center justify-center rounded-md hover:bg-primary-5 focus:outline-none"
          aria-label="Close"
        >
          <Icon name={'Cancel' as Icons} className="text-m text-primary-40" />
        </button>
      </DialogClose>
    </div>
  );
}

// Modal Content
interface ModalContentProps {
  children: React.ReactNode;
  className?: string;
}

function ModalContent({ children, className }: ModalContentProps) {
  return (
    <DialogScrollArea
      data-slot="modal-content"
      className={cn(
        'flex max-h-[252px] flex-col gap-3 px-3',
        '[&_[data-radix-scroll-area-viewport]]:!pt-3',
        '[&_[data-radix-scroll-area-viewport]]:[mask-image:linear-gradient(to_bottom,transparent,black_12px,black_calc(100%-16px),transparent)]',
        className,
      )}
    >
      {children}
    </DialogScrollArea>
  );
}

// Modal Footer
interface ModalFooterProps {
  primaryLabel: string;
  onConfirm: () => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  leftAddon?: React.ReactNode;
}

function ModalFooter({
  primaryLabel,
  onConfirm,
  disabled,
  loading,
  className,
  leftAddon,
}: ModalFooterProps) {
  const isMac = detectOS() === 'mac';
  const isDisabled = disabled || loading;

  // Handle cmd/ctrl+enter keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !isDisabled) {
        e.preventDefault();
        onConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onConfirm, isDisabled]);

  return (
    <div
      data-slot="modal-footer"
      className={cn(
        'flex shrink-0 items-center justify-end border-t border-primary-5 bg-white p-3',
        className,
      )}
    >
      {leftAddon && (
        <div className="max-w-[300px] text-[10px] font-inter text-primary-50 mr-auto">
          {leftAddon}
        </div>
      )}
      <Button
        variant="primary"
        size="sm"
        onClick={onConfirm}
        disabled={isDisabled}
        isLoading={loading}
      >
        {loading ? 'Processing...' : primaryLabel}
        <Kbd tone="dark">{isMac ? '⌘' : 'Ctrl'}</Kbd>
        <Kbd tone="dark">⏎</Kbd>
      </Button>
    </div>
  );
}

export { Modal, ModalTrigger, ModalContainer, ModalHeader, ModalContent, ModalFooter };
